/**
 * Utility functions for handling user contact information
 */

/**
 * Check if a user has any contact information
 * @param {Object} user - User object with contact property
 * @returns {boolean} - True if user has at least one contact method
 */
export const hasContactInfo = (user) => {
  return user && 
         user.contact && 
         Object.values(user.contact).some(value => value && value.trim() !== '');
};

/**
 * Get display name for contact platform
 * @param {string} platform - Contact platform key
 * @returns {string} - Human-readable platform name
 */
export const getContactDisplayName = (platform) => {
  const displayNames = {
    comcUsername: 'COMC',
    discordUsername: 'Discord',
    epackUsername: 'ePack',
    instagramUsername: 'Instagram',
    xUsername: 'X'
  };
  
  return displayNames[platform] || platform;
};

/**
 * Get external URL for contact platform
 * @param {string} platform - Contact platform key
 * @param {string} username - Username on the platform
 * @returns {string|null} - External URL or null if no URL mapping exists
 */
export const getContactPlatformUrl = (platform, username) => {
  if (!username) return null;
  
  const urlMappings = {
    comcUsername: `https://www.comc.com/Users/${username}`,
    epackUsername: `https://www.upperdeckepack.com/Profile/${username}`,
    instagramUsername: `https://www.instagram.com/${username}`,
    xUsername: `https://x.com/${username}`
  };
  
  return urlMappings[platform] || null;
};

/**
 * Get all available contact methods for a user
 * @param {Object} user - User object with contact property
 * @returns {Array} - Array of objects with platform, value, and url properties
 */
export const getUserContactMethods = (user) => {
  if (!hasContactInfo(user)) return [];
  
  return Object.entries(user.contact)
    .filter(([_, value]) => value && value.trim() !== '')
    .map(([platform, value]) => ({
      platform,
      displayName: getContactDisplayName(platform),
      value,
      url: getContactPlatformUrl(platform, value)
    }));
};